import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from "@angular/core";
import Swal from "sweetalert2";

export type ModalidadeVenda = "fracionado" | "integral";
export type TipoServicoCorte = "gratis" | "cortes" | "kilo";

export interface CorteModalidadeSelecao {
  modalidade: ModalidadeVenda;
  tipoServicoCorte?: TipoServicoCorte;
  tamanhoMetros: number;
  precoAtual: number;
  utilizacaoEstoque: number;
  /** `usuario` = clique no serviço; `inicial` = hidratação/preço/estoque. */
  origem?: "inicial" | "usuario";
}

interface OpcaoServicoCorte {
  id: TipoServicoCorte;
  rotulo: string;
  hint: string;
}

@Component({
  selector: "app-corte-modalidade-venda",
  templateUrl: "./corte-modalidade-venda.component.html",
  styleUrls: ["./corte-modalidade-venda.component.css"],
})
export class CorteModalidadeVendaComponent implements OnChanges {
  readonly opcoesServicoCorte: OpcaoServicoCorte[] = [
    {
      id: "gratis",
      rotulo: "Grátis",
      hint: "Não cobra serviço de corte. Apenas um valor unitário por Kilo.",
    },
    {
      id: "cortes",
      rotulo: "Corte",
      hint: "O serviço será cobrado por corte adicionado.",
    },
    {
      id: "kilo",
      rotulo: "Kilo",
      hint: "O serviço será acrescido ao valor unitário do Kilo.",
    },
  ];

  @Input() bloqueado = false;

  @Input() medidasFracionado: number[] = [];
  @Input() medidasIntegral: number[] = [];
  /** Valor total do serviço (`valor_servico_corte` da API). */
  @Input() valorServicoCorte: number | null = null;
  /** Preço do serviço (`preco_servico_corte` da API). */
  @Input() precoServicoCorte: number | null = null;
  @Input() utilizacaoEstoque = 0;

  @Input()
  set modalidadeInicial(v: ModalidadeVenda | null | undefined) {
    if (v === "integral") {
      this._modalidade = v;
    }
  }

  @Input()
  set tamanhoMetrosInicial(v: number | null | undefined) {
    if (v != null && v > 0) {
      this._tamanhoMetrosInicial = v;
    }
  }

  /** Hidratação a partir de `tipo_cobranca` da API (`kilo`, `corte`, `gratis`). */
  @Input()
  set tipoServicoCorteInicial(v: TipoServicoCorte | string | null | undefined) {
    const tipo = this.normalizarTipoServico(v);
    if (tipo) {
      this._tipoServicoCorte = tipo;
    }
  }

  @Output() selecaoChange = new EventEmitter<CorteModalidadeSelecao>();

  private _modalidade: ModalidadeVenda = "integral";
  private _tipoServicoCorte: TipoServicoCorte = "cortes";
  private _tamanhoMetros = 0;
  private _precoAtual = 0;
  private _tamanhoMetrosInicial: number | null = null;
  private dadosInicializados = false;

  get modalidade(): ModalidadeVenda {
    return this._modalidade;
  }

  get tipoServicoCorte(): TipoServicoCorte {
    return this._tipoServicoCorte;
  }

  get tamanhoMetros(): number {
    return this._tamanhoMetros;
  }

  get precoAtual(): number {
    return this._precoAtual;
  }

  get utilizacaoEstoquePct(): number {
    return this.utilizacaoEstoque;
  }

  get opcoesFracionado(): number[] {
    return this.medidasFracionado;
  }

  get temFracionado(): boolean {
    return this.opcoesFracionado.length > 0;
  }

  get rotuloSelecao(): string {
    const servico =
      this.opcoesServicoCorte.find((o) => o.id === this._tipoServicoCorte)
        ?.rotulo ?? "Cortes";
    return `Serviço de corte — ${servico}`;
  }

  get valorServicoExibido(): number {
    const api = Number(this.valorServicoCorte);
    if (Number.isFinite(api) && api >= 0) {
      return api;
    }
    return this._precoAtual;
  }

  get valorServicoFormatado(): string {
    return this.formatarMoeda(this.valorServicoExibido);
  }

  get precoServicoFormatado(): string {
    const v = Number(this.precoServicoCorte);
    return this.formatarMoeda(Number.isFinite(v) && v >= 0 ? v : 0);
  }

  ngOnChanges(changes: SimpleChanges): void {
    const dados =
      changes["medidasFracionado"] ||
      changes["medidasIntegral"] ||
      changes["valorServicoCorte"] ||
      changes["precoServicoCorte"];

    if (dados) {
      this.reconciliarSelecao();
      if (!this.dadosInicializados) {
        this.dadosInicializados = true;
        this.emitirSelecao();
      }
    }

    if (
      (changes["valorServicoCorte"] || changes["precoServicoCorte"]) &&
      this.dadosInicializados
    ) {
      this.atualizarPrecoAtual();
      this.emitirSelecao();
    }

    if (changes["utilizacaoEstoque"] && this.dadosInicializados) {
      this.emitirSelecao();
    }

    if (changes["tipoServicoCorteInicial"]) {
      if (!this.dadosInicializados) {
        this.reconciliarSelecao();
      } else {
        this.atualizarPrecoAtual();
      }
      if (this.dadosInicializados || dados) {
        this.emitirSelecao();
      }
    }
  }

  selecionarModalidade(mod: ModalidadeVenda): void {
    if (this.bloqueado || mod !== "integral") {
      return;
    }
    if (this._modalidade === mod) {
      return;
    }
    this._modalidade = mod;
    this.reconciliarSelecao();
    this.emitirSelecao("usuario");
  }

  selecionarServicoCorte(tipo: TipoServicoCorte): void {
    if (this.bloqueado || this._tipoServicoCorte === tipo) {
      return;
    }

    const rotuloAtual =
      this.opcoesServicoCorte.find((o) => o.id === this._tipoServicoCorte)?.rotulo ??
      "—";
    const rotuloNovo =
      this.opcoesServicoCorte.find((o) => o.id === tipo)?.rotulo ?? "—";

    Swal.fire({
      title: "Alterar tipo de cobrança?",
      html: `Deseja trocar o serviço de corte de <strong>${rotuloAtual}</strong> para <strong>${rotuloNovo}</strong>?`,
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Sim, confirmar",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      focusCancel: true,
    }).then((result) => {
      if (!result.isConfirmed) {
        return;
      }
      this._modalidade = "integral";
      this._tipoServicoCorte = tipo;
      this.reconciliarSelecao();
      this.emitirSelecao("usuario");
    });
  }

  servicoSelecionado(tipo: TipoServicoCorte): boolean {
    return this._modalidade === "integral" && this._tipoServicoCorte === tipo;
  }

  private reconciliarSelecao(): void {
    this._modalidade = "integral";

    const barra = this.medidasIntegral[0] ?? 0;
    this._tamanhoMetros = barra > 0 ? barra : this._tamanhoMetros;

    if (this._tamanhoMetrosInicial != null) {
      this._tamanhoMetrosInicial = null;
    }
    this.atualizarPrecoAtual();
  }

  private atualizarPrecoAtual(): void {
    const api = Number(this.valorServicoCorte);
    if (Number.isFinite(api) && api >= 0) {
      this._precoAtual = api;
      return;
    }
    const preco = Number(this.precoServicoCorte ?? 0);
    const unitario = Number.isFinite(preco) ? preco : 0;
    this._precoAtual = unitario * (this._tamanhoMetros || 0);
  }

  private normalizarTipoServico(
    v: TipoServicoCorte | string | null | undefined
  ): TipoServicoCorte | null {
    const t = String(v ?? "").toLowerCase();
    if (t === "gratis" || t === "abonado") {
      return "gratis";
    }
    if (t === "kilo") {
      return "kilo";
    }
    if (t === "corte" || t === "cortes") {
      return "cortes";
    }
    return null;
  }

  private formatarMoeda(v: number): string {
    const n = Number(v);
    return (Number.isFinite(n) ? n : 0).toLocaleString("pt-BR", {
      style: "currency",
      currency: "BRL",
    });
  }

  private emitirSelecao(origem: "inicial" | "usuario" = "inicial"): void {
    this.selecaoChange.emit({
      modalidade: this._modalidade,
      tipoServicoCorte: this._tipoServicoCorte,
      tamanhoMetros: this._tamanhoMetros,
      precoAtual: this.valorServicoExibido,
      utilizacaoEstoque: this.utilizacaoEstoquePct,
      origem,
    });
  }
}
